import React from 'react';
import { useStore } from '@/store';
import { Stream, Workspace } from '@/store';
import { Button } from '@/components/ui/button';

interface ConfigPanelProps {
  type: 'stream' | 'studio' | 'workspace';
}

// Type guard functions
function isStream(item: Stream | Workspace): item is Stream {
  return 'stream_id' in item;
}

function isWorkspace(item: Stream | Workspace): item is Workspace {
  return 'workspace_id' in item;
}

// Helper functions to get properties based on type
function getId(item: Stream | Workspace): number {
  return isStream(item) ? item.stream_id : item.workspace_id;
}

function getName(item: Stream | Workspace): string {
  return isStream(item) ? item.stream_topic : item.workspace_name;
}

function getDescription(item: Stream | Workspace): string {
  return isStream(item) ? item.stream_description : item.workspace_description;
}

export function ConfigPanel({ type }: ConfigPanelProps) {
  const {
    selectedStreamId,
    selectedWorkspaceId,
    userProfile,
    streams,
    workspaces
  } = useStore();

  const [description, setDescription] = React.useState<string>('');
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  // Determine if we're dealing with a stream or workspace
  const isStreamType = type === 'stream';
  
  // Get the appropriate items based on type
  const items = isStreamType
    ? streams || (userProfile?.streams || []) 
    : workspaces || (userProfile?.workspaces || []);
  
  // Find the selected item
  const selectedItem = items.find(item => 
    getId(item) === (isStreamType ? selectedStreamId : selectedWorkspaceId)
  );

  // Initialize description from selected item
  React.useEffect(() => {
    if (selectedItem) {
      setDescription(getDescription(selectedItem));
    }
  }, [selectedItem]);

  if (!selectedItem) {
    return (
      <div className="p-4 h-full flex items-center justify-center">
        <p className="text-gray-500">No {isStreamType ? 'stream' : 'workspace'} selected</p>
      </div>
    );
  }

  const handleDescriptionUpdate = async () => {
    const id = isStreamType ? selectedStreamId : selectedWorkspaceId;
    if (!id) return;

    setIsSaving(true);
    try {
      const endpoint = isStreamType ? 'streams' : 'workspaces';
      const response = await fetch(`/api/v1/${endpoint}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description })
      });

      if (!response.ok) {
        throw new Error(`Failed to update ${isStreamType ? 'stream' : 'workspace'} description`);
      }
    } catch (error) {
      console.error(`Failed to update ${isStreamType ? 'stream' : 'workspace'} description:`, error);
    } finally {
      setIsSaving(false);
    }
  };

  // Get display name based on type
  const displayType = isStreamType ? 'Stream' : type === 'studio' ? 'Studio' : 'Workspace';

  return (
    <div className="p-4 h-full">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-medium">{displayType} Settings</h2>
          <p className="text-sm text-gray-500">
            Configure your {isStreamType ? 'stream' : type === 'studio' ? 'studio' : 'workspace'} settings and preferences
          </p>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Name</h3>
              <p className="text-sm">{getName(selectedItem)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-2">Description</h3>
              <textarea
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                placeholder={`Enter ${isStreamType ? 'stream' : type === 'studio' ? 'studio' : 'workspace'} description...`}
                className="w-full min-h-[100px] p-2 border rounded-md"
              />
            </div>
            {/* Additional settings can be added here */}
          </div>
        </div>
        <div className="px-6 py-4 border-t">
          <Button 
            onClick={handleDescriptionUpdate} 
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
} 